import React, { Suspense, useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';

import { ErrorBoundary } from 'react-error-boundary';
import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';

import { AIMateAnalyticsContext } from '@atlaskit/analytics-namespaced-context';
import { type UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button, { IconButton } from '@atlaskit/button/new';
import { cssMap, cx } from '@atlaskit/css';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { FlagsProvider } from '@atlaskit/flag';
import AddIcon from '@atlaskit/icon/core/add';
import CrossIcon from '@atlaskit/icon/core/cross';
import Modal, { ModalHeader, ModalTitle } from '@atlaskit/modal-dialog';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Flex, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';
import { ContextualAnalyticsData } from '@atlassian/analytics-bridge';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { AnalyticsContextWithSourceAndProperties } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics-properties-context';
import { logException } from '@atlassian/conversation-assistant-instrumentation/services/sentry';
import {
	ExperienceFailure,
	ExperienceStart,
	ExperienceSuccess,
} from '@atlassian/conversation-assistant-instrumentation/ui/ExperienceTracker';
import { type Agent } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { ChatGenericError } from '@atlassian/conversation-assistant-ui-components/ui/ChatGenericError';
import { SearchInput } from '@atlassian/conversation-assistant-ui-components/ui/SearchInput';
import { AtlassianRelayEnvironmentProvider } from '@atlassian/relay-environment-provider';

import { ROVO_AGENTS_FE_TEAM_NAME } from '../../common/constants/private.ts';
import {
	type BrowseAgentsFullscreenProps,
	type BrowseAgentsModalProps,
	type BrowseAgentsModalPropsWithoutOnClose,
	type BrowseAgentsSidebarProps,
	type BrowseCategory,
} from '../../common/types.ts';
import { AgentCreateButton } from '../../controllers/agent-create-button/index.tsx';
import { useBrowseAgentsUIStore } from '../../controllers/browse-agents-ui/index.tsx';

import AgentDeleteConfirm, { AgentDeleteFlags } from './agent-delete-confirm/index.tsx';
import { AgentsListFullscreen } from './agents-list-fullscreen/index.tsx';
import { BrowseAgentProviders } from './browse-agent-providers/index.tsx';
import { BrowseAgentTabs } from './browse-agent-tabs/index.tsx';
import { BrowseAgentsContent, getSearchAnalyticsPayload } from './browse-agents-content/index.tsx';
import { ViewAgentModal } from './browse-agents-content/view-agent/index.tsx';
import { BrowseAgentsFullscreenHeader } from './browse-agents-fullscreen-header/index.tsx';
import { BrowseAgentsHeader as BrowseAgentsHeaderRefresh } from './browse-agents-header-refresh/index.tsx';
import messages from './messages.ts';
import { PaginatedAgentsList } from './paginated-agents-list/index.tsx';

const useTabSelection = (props?: { browseCategory?: BrowseCategory }) => {
	const [selectedTab, setSelectedTab] = useState<BrowseCategory>(
		props?.browseCategory ?? 'all-agents',
	);

	return { selectedTab, setSelectedTab };
};

const styles = cssMap({
	agentsContent: {
		flex: 4,
		height: '100%',
	},
	modalSidebar: {
		width: '140px',
		'@media (min-width: 48rem)': {
			width: '262px',
		},
		height: '100%',
		overflow: 'auto',
		paddingBottom: token('space.200'),
	},
	contentWrapper: {
		paddingTop: token('space.300'),
		paddingRight: token('space.300'),
		paddingLeft: token('space.250'),
		gap: token('space.300'),
		overflow: 'hidden',
	},
});

const MODAL_WIDTH = '1248px';

const modalStyles = cssMap({
	loadingContainer: {
		marginTop: token('space.400'),
		minHeight: '300px',
	},
	borderDivider: {
		width: '100%',
		height: '5px',
		borderBottomColor: token('color.border'),
		borderBottomStyle: 'solid',
		borderBottomWidth: token('border.width'),
	},
	searchWrapper: {
		paddingTop: token('space.100'),
		backgroundColor: token('elevation.surface.overlay'),
	},
	searchRow: {
		paddingTop: token('space.100'),
		backgroundColor: token('elevation.surface.overlay'),
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
	},
	searchInputGrow: {
		flexGrow: 1,
	},
	agentsContentWrapper: {
		height: '100%',
		overflow: 'hidden',
	},
	outerWrapper: {
		height: '100%',
		display: 'flex',
		flexDirection: 'column',
	},
});

const BrowseAgentsModalInternalNew = ({
	onClose,
	siteId,
	onClickChat,
	onClickAgent,
	handleClickConversationStarter,
	assistanceServiceParams,
	agentTabs,
	agentCardRole,
	getAgentCardAriaLabel,
	browseCategory,
	customAgentDropdownMenuOptions,
	renderAgentCardFooter,
	renderAgentProfileCta,
	renderAgentProfileAdditionalContent,
	wrapInModal = true,
	customTabs,
	enableJiraAgentFields,
	onClickCreateAgent,
}: BrowseAgentsModalProps) => {
	const { formatMessage } = useIntl();
	const { sendAnalyticsEvent } = useAnalytics();
	const [{ selectedAgent }, { setSelectedAgent, resetBrowseAgentsUI, setAgentDeletion }] =
		useBrowseAgentsUIStore();

	const { selectedTab, setSelectedTab } = useTabSelection({ browseCategory });

	const [searchValue, setSearchValue] = useState('');

	useEffect(() => {
		sendAnalyticsEvent({
			eventType: 'screen',
			name: 'browseAgentsModal',
		});
	}, [sendAnalyticsEvent]);

	const handleCloseModal = useCallback(
		(analyticsEvent: UIAnalyticsEvent) => {
			resetBrowseAgentsUI();
			onClose();
			sendAnalyticsEvent(
				analyticsEvent.update({
					actionSubjectId: 'browseAgentsCloseModalButton',
				}),
			);
		},
		[resetBrowseAgentsUI, onClose, sendAnalyticsEvent],
	);

	const onDeleteAgent = useCallback(
		async (agent: Agent) => {
			if (agent) {
				setAgentDeletion({
					agent,
					status: 'none',
				});
			}
		},
		[setAgentDeletion],
	);

	const onGoBack = useCallback(() => {
		setSelectedAgent(null);
	}, [setSelectedAgent]);

	const handleSearchChange = useCallback(
		(searchValue: string) => {
			setSearchValue(searchValue);

			sendAnalyticsEvent(
				getSearchAnalyticsPayload({
					searchValue,
					viewContext: 'agents-modal',
					category: selectedTab,
				}),
			);
		},
		[selectedTab, sendAnalyticsEvent],
	);

	const handleBrowseTabChange = useCallback(
		(tab: BrowseCategory) => {
			setSelectedTab(tab);
		},
		[setSelectedTab],
	);

	// Memoize common content to avoid duplication
	const loadingFallback = useMemo(
		() => (
			<Inline alignInline="center" xcss={modalStyles.loadingContainer}>
				<Button testId="browse-agents-load-more-button" isDisabled>
					<Inline space="space.100" alignBlock="center">
						<Spinner size="small" interactionName="loading-paginated-agents" />
						{formatMessage(messages.loadingAgents)}
					</Inline>
				</Button>
			</Inline>
		),
		[formatMessage],
	);

	const CustomTabContent = customTabs?.[selectedTab]?.Content;

	const searchPlaceholderText = useMemo(
		() => customTabs?.[selectedTab]?.searchPlaceholder ?? formatMessage(messages.searchPlaceholder),
		[customTabs, selectedTab, formatMessage],
	);

	const customTabContentProps = useMemo(
		() => ({
			searchValue,
			onSearch: handleSearchChange,
			onTabChange: handleBrowseTabChange,
			customTabId: selectedTab,
			siteId,
			onClose,
			assistanceServiceParams,
			onClickChat,
			onClickAgent,
			handleClickConversationStarter,
			onDeleteAgent,
			agentCardRole,
			getAgentCardAriaLabel,
			customAgentDropdownMenuOptions,
			renderAgentCardFooter,
			enableJiraAgentFields,
		}),
		[
			searchValue,
			handleSearchChange,
			handleBrowseTabChange,
			selectedTab,
			siteId,
			onClose,
			assistanceServiceParams,
			onClickChat,
			onClickAgent,
			handleClickConversationStarter,
			onDeleteAgent,
			agentCardRole,
			getAgentCardAriaLabel,
			customAgentDropdownMenuOptions,
			renderAgentCardFooter,
			enableJiraAgentFields,
		],
	);
	const isRecommendationsM1 = FeatureGates.getExperimentValue(
		'jira_agent_recommendations_m1',
		'isEnabled',
		false,
	);

	const agentsContent = useMemo(
		() => (
			<Stack xcss={modalStyles.agentsContentWrapper}>
				{isRecommendationsM1 ? (
					<Box xcss={modalStyles.searchRow}>
						<Box xcss={modalStyles.searchInputGrow}>
							<SearchInput
								placeholderText={searchPlaceholderText}
								onSearch={handleSearchChange}
								isCompact
							/>
						</Box>
						<AgentCreateButton
							siteId={siteId}
							buttontext={messages.createAgentButton}
							touchpointSource={'browse-agents-modal-search-row'}
							analyticsPayload={{
								actionSubjectId: 'browseAgentsCreateAgentButtonSearchRow',
								attributes: {
									interactionSource: 'browse-agents-modal-search-row',
								},
							}}
							iconBefore={AddIcon}
							onClickCreateAgent={onClickCreateAgent}
						/>
					</Box>
				) : (
					<Box xcss={cx(modalStyles.searchWrapper)}>
						<SearchInput
							placeholderText={searchPlaceholderText}
							onSearch={handleSearchChange}
							isCompact
						/>
					</Box>
				)}
				<ErrorBoundary
					fallbackRender={({ error }) => (
						<>
							<ExperienceFailure
								error={error}
								name={ExperienceName.AGENTS_MODAL_AND_SIDEBAR_LIST}
							/>
							<ChatGenericError errorType="generic" />
						</>
					)}
					onError={(error) => {
						logException({
							exception: error,
							name: ExperienceName.AGENTS_MODAL_AND_SIDEBAR_LIST,
							tags: {
								siteId: siteId,
								teamName: ROVO_AGENTS_FE_TEAM_NAME,
								category: selectedTab,
							},
						});
					}}
				>
					<Suspense fallback={loadingFallback}>
						<ExperienceStart name={ExperienceName.AGENTS_MODAL_AND_SIDEBAR_LIST} />
						{CustomTabContent ? (
							<CustomTabContent {...customTabContentProps} />
						) : (
							<BrowseAgentsContent
								uiContext="agents-modal"
								assistanceServiceParams={assistanceServiceParams}
								onClickChat={onClickChat}
								onClickAgent={onClickAgent}
								onDeleteAgent={onDeleteAgent}
								agentCardRole={agentCardRole}
								getAgentCardAriaLabel={getAgentCardAriaLabel}
								searchValue={searchValue}
								selectedTab={selectedTab}
								onTabChange={handleBrowseTabChange}
								renderAgentCardFooter={renderAgentCardFooter}
								customAgentDropdownMenuOptions={customAgentDropdownMenuOptions}
								enableJiraAgentFields={enableJiraAgentFields}
							/>
						)}
						<ExperienceSuccess name={ExperienceName.AGENTS_MODAL_AND_SIDEBAR_LIST} />
					</Suspense>
				</ErrorBoundary>
			</Stack>
		),
		[
			CustomTabContent,
			customTabContentProps,
			handleSearchChange,
			loadingFallback,
			searchPlaceholderText,
			assistanceServiceParams,
			onClickChat,
			onClickAgent,
			onDeleteAgent,
			agentCardRole,
			getAgentCardAriaLabel,
			searchValue,
			selectedTab,
			handleBrowseTabChange,
			siteId,
			customAgentDropdownMenuOptions,
			renderAgentCardFooter,
			enableJiraAgentFields,
			isRecommendationsM1,
			onClickCreateAgent,
		],
	);

	const modalHeader = useMemo(() => {
		return (
			<>
				{/* eslint-disable-next-line @atlaskit/design-system/use-modal-title */}
				<ModalHeader hasCloseButton={false}>
					<BrowseAgentsHeaderRefresh
						titleElement={<ModalTitle>{formatMessage(messages.modalTitle)}</ModalTitle>}
						subtitleElement={<Text>{formatMessage(messages.modalSubtitle)}</Text>}
						onClose={handleCloseModal}
						siteId={siteId}
						onClickCreateAgent={onClickCreateAgent}
					/>
				</ModalHeader>
				<Box xcss={modalStyles.borderDivider} />
			</>
		);
	}, [formatMessage, handleCloseModal, siteId, onClickCreateAgent]);

	const modalBody = useMemo(() => {
		return (
			<Inline xcss={styles.contentWrapper} testId="rovo-browse-agents-modal-content">
				<Box xcss={styles.modalSidebar}>
					<BrowseAgentTabs
						agentTabs={agentTabs}
						customTabs={customTabs}
						viewContext={'agents-modal'}
						siteId={siteId}
						selectedTab={selectedTab}
						onTabChange={handleBrowseTabChange}
					/>
					<PaginatedAgentsList
						assistanceServiceParams={assistanceServiceParams}
						queryInput={{ onlyFavouriteAgents: true }}
						sectionTitle={formatMessage(messages.favouritesSectionTitle)}
						onClickChat={onClickChat}
						onClickAgent={onClickAgent}
					/>
				</Box>
				<Box xcss={styles.agentsContent}>{agentsContent}</Box>
			</Inline>
		);
	}, [
		agentsContent,
		agentTabs,
		customTabs,
		siteId,
		selectedTab,
		handleBrowseTabChange,
		assistanceServiceParams,
		formatMessage,
		onClickChat,
		onClickAgent,
	]);

	const browseAgentsModal = useMemo(() => {
		return wrapInModal ? (
			<Modal
				width={MODAL_WIDTH}
				height={'100%'}
				shouldCloseOnEscapePress
				onClose={(_, analyticsEvent) => handleCloseModal(analyticsEvent)}
			>
				{modalHeader}

				{modalBody}
			</Modal>
		) : (
			<>
				{modalHeader}
				{modalBody}
			</>
		);
	}, [modalHeader, modalBody, handleCloseModal, wrapInModal]);

	return (
		<Box xcss={modalStyles.outerWrapper}>
			{selectedAgent ? (
				<ViewAgentModal
					agentId={selectedAgent.id}
					assistanceServiceParams={assistanceServiceParams}
					handleClickConversationStarter={handleClickConversationStarter}
					onClickChat={onClickChat}
					onClose={onClose}
					onDeleteAgent={onDeleteAgent}
					onGoBack={onGoBack}
					renderAgentProfileCta={renderAgentProfileCta}
					renderAgentProfileAdditionalContent={renderAgentProfileAdditionalContent}
					wrapInModal={wrapInModal}
					enableJiraAgentFields={enableJiraAgentFields}
				/>
			) : (
				browseAgentsModal
			)}
			<AgentDeleteConfirm
				onClose={() => {
					setSelectedAgent(null);
				}}
				assistanceServiceParams={assistanceServiceParams}
			/>
			<AgentDeleteFlags assistanceServiceParams={assistanceServiceParams} />
		</Box>
	);
};

const useBrowseAgentsInitialization = (
	props: BrowseAgentsModalProps & {
		screenName: string;
	},
) => {
	const { sendAnalyticsEvent } = useAnalytics();
	const [_, { setSelectedTab, resetBrowseAgentsUI }] = useBrowseAgentsUIStore();
	const {
		assistanceServiceParams: { touchpointSource },
	} = props;

	// Set the selected tab based on the browseCategory prop
	useLayoutEffect(() => {
		if (props.browseCategory) {
			setSelectedTab(props.browseCategory);
		}
	}, [setSelectedTab, props.browseCategory]);

	useEffect(() => {
		sendAnalyticsEvent({
			action: 'viewed',
			actionSubject: props.screenName,
			attributes: { touchpointSource },
		});

		return () => {
			sendAnalyticsEvent({
				action: 'dismissed',
				actionSubject: props.screenName,
				attributes: { touchpointSource },
			});
		};
	}, [sendAnalyticsEvent, touchpointSource, props.screenName]);

	useEffect(() => {
		return () => {
			resetBrowseAgentsUI();
		};
	}, [resetBrowseAgentsUI]);

	return null;
};

export const BrowseAgentsModal = (props: BrowseAgentsModalProps): React.JSX.Element => {
	di(AtlassianRelayEnvironmentProvider);
	useBrowseAgentsInitialization({
		...props,
		screenName: 'browseAgentModal',
	});

	return (
		<BrowseAgentProviders siteId={props.siteId} product={props.assistanceServiceParams.product}>
			<AtlassianRelayEnvironmentProvider>
				<AnalyticsContextWithSourceAndProperties source="AIMate">
					<AIMateAnalyticsContext
						data={{ attributes: { experienceId: props.assistanceServiceParams.experienceId } }}
					>
						<BrowseAgentsModalInternalNew {...props} />
					</AIMateAnalyticsContext>
				</AnalyticsContextWithSourceAndProperties>
			</AtlassianRelayEnvironmentProvider>
		</BrowseAgentProviders>
	);
};

const sidebarStyles = cssMap({
	browseAgentsView: {
		maxWidth: '100%',
		width: '100%',
		overflow: 'hidden',
		paddingTop: token('space.200'),
		paddingBottom: token('space.200'),
		justifyContent: 'flex-start',
	},
	// removes top padding to allow correct header spacing
	browseAgentsViewRefreshContent: {
		overflow: 'auto',
		paddingBottom: token('space.200'),
		justifyContent: 'flex-start',
	},
	browseAgentsViewRefreshContentFocusOffset: {
		paddingBottom: token('space.0'),
	},
	browseAgentsViewRefreshRoot: {
		maxWidth: '100%',
		width: '100%',
		overflow: 'hidden',
	},
	browseAgentsContent: {
		alignSelf: 'stretch',
		justifySelf: 'stretch',
		overflow: 'auto',
	},
	browseAgentsContentFocusOffset: {
		paddingTop: token('space.050'),
	},
	loadingAgentsContainer: {
		marginTop: token('space.400'),
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		minHeight: '300px',
		width: '100%',
	},
	agentTabsWrapperRefresh: {
		paddingTop: token('space.200'),
	},
	agentTabsWrapperRefreshFocusOffset: {
		paddingTop: token('space.250'),
	},
	contentWrapperRefresh: {
		paddingLeft: token('space.200'),
		paddingRight: token('space.200'),
	},
});

export const BrowseAgentsSidebar = (props: BrowseAgentsSidebarProps): React.JSX.Element => {
	di(AtlassianRelayEnvironmentProvider);
	const {
		onClose,
		siteId,
		onClickChat,
		handleClickConversationStarter,
		assistanceServiceParams,
		agentTabs,
		titleElement,
		chatRefreshHeader,
		singleColumn,
		browseCategory,
		getAgentCardAriaLabel,
	} = props;

	const [{ selectedAgent }, { setSelectedAgent, setAgentDeletion }] = useBrowseAgentsUIStore();
	const { selectedTab, setSelectedTab } = useTabSelection({ browseCategory });
	const { formatMessage } = useIntl();
	const { sendAnalyticsEvent } = useAnalytics();
	const [searchValue, setSearchValue] = useState('');
	useBrowseAgentsInitialization({
		...props,
		screenName: 'browseAgentsSidebar',
	});

	const onDeleteAgent = async (agent: Agent, source: string) => {
		if (agent) {
			setAgentDeletion({
				agent,
				status: 'none',
			});

			sendAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'deleteAgentButton',
				attributes: { agentId: agent.id, source },
			});
		}
	};

	const onViewAgentClose = useCallback(() => {
		setSelectedAgent(null);
	}, [setSelectedAgent]);

	const handleSearchChange = useCallback(
		(searchValue: string) => {
			setSearchValue(searchValue);

			sendAnalyticsEvent(
				getSearchAnalyticsPayload({
					searchValue,
					viewContext: 'agents-sidebar',
					category: selectedTab,
				}),
			);
		},
		[sendAnalyticsEvent, selectedTab],
	);

	const searchAndTabs = useMemo(() => {
		return (
			<Box>
				<Flex gap="space.100" xcss={sidebarStyles.contentWrapperRefresh}>
					<SearchInput
						placeholderText={formatMessage(messages.searchPlaceholder)}
						onSearch={handleSearchChange}
						isCompact={true}
					/>
					<BrowseAgentsModalTrigger
						siteId={siteId}
						onClickChat={onClickChat}
						handleClickConversationStarter={handleClickConversationStarter}
						assistanceServiceParams={assistanceServiceParams}
						agentTabs={agentTabs}
						agentCardRole={props.agentCardRole}
					/>

					<AgentCreateButton
						siteId={siteId}
						isIconButton
						buttontext={messages.createAgentButton}
						analyticsPayload={{
							action: 'clicked',
							actionSubject: 'button',
							actionSubjectId: 'browseAgentsSidebarCreateButton',
						}}
						touchpointSource="browse-agents-sidebar"
					/>
				</Flex>
				<Box
					xcss={cx(
						sidebarStyles.agentTabsWrapperRefresh,
						fg('rovo_agents_browse_sidebar_focus_offset') &&
							sidebarStyles.agentTabsWrapperRefreshFocusOffset,
					)}
				>
					<BrowseAgentTabs
						agentTabs={agentTabs}
						viewContext={'agents-sidebar'}
						siteId={siteId}
						selectedTab={selectedTab}
						onTabChange={setSelectedTab}
					/>
				</Box>
			</Box>
		);
	}, [
		agentTabs,
		siteId,
		selectedTab,
		setSelectedTab,
		formatMessage,
		handleSearchChange,
		onClickChat,
		handleClickConversationStarter,
		assistanceServiceParams,
		props.agentCardRole,
	]);

	const browseAgentsContent = (
		<>
			<Box
				xcss={cx(
					sidebarStyles.browseAgentsContent,
					fg('rovo_agents_browse_sidebar_focus_offset') &&
						sidebarStyles.browseAgentsContentFocusOffset,
				)}
			>
				{searchAndTabs}

				<ErrorBoundary
					fallbackRender={({ error }) => (
						<>
							<ExperienceFailure
								error={error}
								name={ExperienceName.AGENTS_MODAL_AND_SIDEBAR_LIST}
							/>
							<ChatGenericError errorType="generic" />
						</>
					)}
					onError={(error) => {
						logException({
							exception: error,
							name: ExperienceName.AGENTS_MODAL_AND_SIDEBAR_LIST,
							tags: {
								siteId: siteId,
								teamName: ROVO_AGENTS_FE_TEAM_NAME,
								category: selectedTab,
							},
						});
					}}
				>
					<ExperienceStart name={ExperienceName.AGENTS_MODAL_AND_SIDEBAR_LIST} />
					<Suspense
						fallback={
							<Inline
								alignBlock="center"
								alignInline="center"
								xcss={sidebarStyles.loadingAgentsContainer}
							>
								<Button testId="browse-agents-load-more-button" isDisabled>
									<Inline space="space.100" alignBlock="center">
										<Spinner size="small" interactionName="loading-paginated-agents" />
										{formatMessage(messages.loadingAgents)}
									</Inline>
								</Button>
							</Inline>
						}
					>
						<Box
							xcss={
								fg('rovo_agents_browse_sidebar_focus_offset')
									? undefined
									: sidebarStyles.contentWrapperRefresh
							}
						>
							<BrowseAgentsContent
								uiContext="agents-sidebar"
								singleColumn={singleColumn}
								assistanceServiceParams={assistanceServiceParams}
								onClickChat={onClickChat}
								onDeleteAgent={onDeleteAgent}
								searchValue={searchValue}
								selectedTab={selectedTab}
								onTabChange={setSelectedTab}
								getAgentCardAriaLabel={getAgentCardAriaLabel}
							/>
						</Box>
					</Suspense>

					<ExperienceSuccess name={ExperienceName.AGENTS_MODAL_AND_SIDEBAR_LIST} />
				</ErrorBoundary>
			</Box>
			<AgentDeleteConfirm
				onClose={() => {
					setSelectedAgent(null);
				}}
				assistanceServiceParams={assistanceServiceParams}
			/>
			<AgentDeleteFlags assistanceServiceParams={assistanceServiceParams} />
			{selectedAgent && (
				<ViewAgentModal
					agentId={selectedAgent.id}
					assistanceServiceParams={assistanceServiceParams}
					handleClickConversationStarter={handleClickConversationStarter}
					onClickChat={onClickChat}
					onClose={onViewAgentClose}
					onDeleteAgent={onDeleteAgent}
					onGoBack={onViewAgentClose}
				/>
			)}
		</>
	);

	const browseAgentsView = chatRefreshHeader ? (
		<Stack testId="rovo-browse-agents-panel" xcss={sidebarStyles.browseAgentsViewRefreshRoot}>
			{chatRefreshHeader}

			<Stack
				xcss={cx(
					sidebarStyles.browseAgentsViewRefreshContent,
					fg('rovo_agents_browse_sidebar_focus_offset') &&
						sidebarStyles.browseAgentsViewRefreshContentFocusOffset,
				)}
				space={fg('rovo_agents_browse_sidebar_focus_offset') ? undefined : 'space.150'}
			>
				{browseAgentsContent}
			</Stack>
		</Stack>
	) : (
		<Stack
			testId="rovo-browse-agents-panel"
			space="space.150"
			xcss={sidebarStyles.browseAgentsView}
		>
			<Box>
				<Inline alignBlock="center" spread="space-between" space="space.050">
					{titleElement}
					<IconButton
						isTooltipDisabled={false}
						icon={(props) => <CrossIcon {...props} color="var(--ds-icon)" size="small" />}
						appearance="subtle"
						onClick={onClose}
						label={formatMessage(messages.closeButton)}
					/>
				</Inline>
			</Box>
			{browseAgentsContent}
		</Stack>
	);

	return (
		<BrowseAgentProviders siteId={siteId} product={assistanceServiceParams.product}>
			<AtlassianRelayEnvironmentProvider>{browseAgentsView}</AtlassianRelayEnvironmentProvider>
		</BrowseAgentProviders>
	);
};

const fullscreenStyles = cssMap({
	root: {
		maxWidth: '100%',
		width: '100%',
		overflow: 'hidden',
		paddingBottom: token('space.300'),
	},
	headerWrapper: {
		alignSelf: 'stretch',
		justifySelf: 'stretch',
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.200'),
		width: '100%',
	},
	contentWrapper: {
		overflow: 'auto',
	},
	headerContent: {
		paddingInline: token('space.200'),
	},
});

/**
 * @private
 * @deprecated Use BrowseAgentsFullscreenNew instead. See thread for more info: https://atlassian.slack.com/archives/C08UY102KSA/p1779865548308599
 */
export const BrowseAgentsFullscreen = (props: BrowseAgentsFullscreenProps): React.JSX.Element => {
	const {
		siteId,
		onClickChat,
		handleClickConversationStarter,
		assistanceServiceParams,
		agentTabs,
		agentCardRole,
	} = props;
	const { touchpointSource } = assistanceServiceParams;

	const [{ selectedAgent, selectedTab }, { setSelectedAgent, setAgentDeletion }] =
		useBrowseAgentsUIStore();
	const { formatMessage } = useIntl();
	const { sendAnalyticsEvent } = useAnalytics();
	const [searchValue, setSearchValue] = useState('');

	useEffect(() => {
		sendAnalyticsEvent({
			action: 'viewed',
			actionSubject: 'browseAgentsFullscreen',
			attributes: { touchpointSource },
		});
	}, [sendAnalyticsEvent, touchpointSource]);

	const onDeleteAgent = async (agent: Agent, source: string) => {
		setAgentDeletion({
			agent,
			status: 'none',
		});

		sendAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'deleteAgentButton',
			attributes: { agentId: agent.id, source },
		});
	};

	const onCloseSelectedAgent = useCallback(() => {
		setSelectedAgent(null);
	}, [setSelectedAgent]);

	const handleSearchChange = useCallback(
		(searchValue: string) => {
			setSearchValue(searchValue);

			sendAnalyticsEvent(
				getSearchAnalyticsPayload({
					searchValue,
					viewContext: 'agents-fullscreen',
					category: selectedTab,
				}),
			);
		},
		[sendAnalyticsEvent, selectedTab],
	);

	const browseAgentsContent = (
		<Flex xcss={fullscreenStyles.headerWrapper}>
			<Stack space="space.200" xcss={fullscreenStyles.headerContent}>
				<BrowseAgentsFullscreenHeader siteId={siteId} />
				<Inline space="space.100" alignBlock="center">
					<SearchInput
						placeholderText={formatMessage(messages.searchPlaceholder)}
						onSearch={handleSearchChange}
						isCompact={true}
					/>
					<BrowseAgentsModalTrigger
						siteId={siteId}
						onClickChat={onClickChat}
						handleClickConversationStarter={handleClickConversationStarter}
						assistanceServiceParams={assistanceServiceParams}
						agentTabs={agentTabs}
						agentCardRole={agentCardRole}
					/>
				</Inline>
			</Stack>

			<AgentsListFullscreen
				assistanceServiceParams={assistanceServiceParams}
				searchValue={searchValue}
				onClickChat={onClickChat}
			/>

			<AgentDeleteConfirm
				onClose={onCloseSelectedAgent}
				assistanceServiceParams={assistanceServiceParams}
			/>
			<AgentDeleteFlags assistanceServiceParams={assistanceServiceParams} />
			{selectedAgent && (
				<ViewAgentModal
					agentId={selectedAgent.id}
					assistanceServiceParams={assistanceServiceParams}
					handleClickConversationStarter={handleClickConversationStarter}
					onClickChat={onClickChat}
					onClose={onCloseSelectedAgent}
					onDeleteAgent={onDeleteAgent}
					onGoBack={onCloseSelectedAgent}
				/>
			)}
		</Flex>
	);

	const browseAgentsView = (
		<Stack testId="rovo-browse-fullscreen-agents-panel" xcss={fullscreenStyles.root}>
			<Flex xcss={fullscreenStyles.contentWrapper}>{browseAgentsContent}</Flex>
		</Stack>
	);

	return (
		<BrowseAgentProviders siteId={siteId} product={assistanceServiceParams.product}>
			<AtlassianRelayEnvironmentProvider>
				<FlagsProvider>{browseAgentsView}</FlagsProvider>
			</AtlassianRelayEnvironmentProvider>
		</BrowseAgentProviders>
	);
};

export const BrowseAgentsModalTrigger = ({
	siteId,
	onClickChat,
	handleClickConversationStarter,
	assistanceServiceParams,
	agentTabs,
	agentCardRole,
}: BrowseAgentsModalPropsWithoutOnClose): React.JSX.Element => {
	di();

	const { formatMessage } = useIntl();
	const [showBrowseModal, setShowBrowseModal] = useState(false);

	const handleCloseModal = useCallback(() => {
		setShowBrowseModal(false);
	}, []);

	return (
		<Box>
			{showBrowseModal && (
				<ContextualAnalyticsData sourceName="BrowseAgentsModalTrigger" sourceType="Modal">
					<BrowseAgentsModal
						onClose={handleCloseModal}
						siteId={siteId}
						onClickChat={onClickChat}
						handleClickConversationStarter={handleClickConversationStarter}
						assistanceServiceParams={assistanceServiceParams}
						agentTabs={agentTabs}
						agentCardRole={agentCardRole}
					/>
				</ContextualAnalyticsData>
			)}
			<Button onClick={() => setShowBrowseModal(true)}>
				{formatMessage(messages.browseAgentsButton)}
			</Button>
		</Box>
	);
};
